import { createTheme } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

declare global {
  interface Window {
    __THEME_CONFIG__?: Record<string, unknown>;
  }
}

declare module '@mui/material/styles' {
  interface Theme {
    custom: {
      outlinedBorder: string;
      borderDefault: string;
      borderFocused: string;
      borderDivider: string;
      chipBg: string;
      errorBg: string;
      textBody: string;
      textMuted: string;
      disabled: string;
      footerLinkHover: string;
    };
  }
  interface ThemeOptions {
    custom?: Partial<Theme['custom']>;
  }
}

const themeConfig = window.__THEME_CONFIG__ ?? {};
const baseTheme = createTheme({
  ...themeConfig,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1200,
      lg: 1200,
      xl: 1536,
    },
  },
});

const theme = createTheme(baseTheme, {
  palette: {
    primary: {
      main: '#004B93',
      dark: 'rgb(0, 51, 102)',
      light: 'rgb(49, 108, 167)',
      contrastText: '#EEF5FA',
    },
    secondary: {
      main: '#02213f',
      dark: 'rgb(1, 23, 44)',
      light: 'rgb(51, 76, 100)',
      contrastText: '#EEF5FA',
    },
    error: {
      main: '#d40511',
      dark: 'rgb(148, 3, 11)',
      light: 'rgb(220, 53, 63)',
      contrastText: '#EEF5FA',
    },
    warning: {
      main: '#ffc000',
      dark: 'rgb(178, 134, 0)',
      light: 'rgb(255, 204, 49)',
      contrastText: '#EEF5FA',
    },
    info: {
      main: '#00b093',
      dark: 'rgb(0, 123, 102)',
      light: 'rgb(49, 191, 168)',
      contrastText: '#EEF5FA',
    },
    success: {
      main: '#95c620',
      dark: 'rgb(104, 138, 22)',
      light: 'rgb(169, 209, 75)',
      contrastText: '#EEF5FA',
    },
    text: {
      primary: '#22272F',
      secondary: '#495057',
      disabled: '#809DB3',
    },
    divider: '#dde7ee',
    background: {
      paper: '#ffffff',
      default: '#f3f5f7',
    },
    action: {
      hover: '#EEF5FA',
      disabled: '#809DB3',
      disabledBackground: '#C9D7DF',
    },
  },
  typography: {
    fontFamily: 'Roboto,sans-serif',
    htmlFontSize: 16,
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 300,
    fontWeightMedium: 700,
    fontWeightBold: 800,
    h1: {
      fontSize: '1.75rem',
      lineHeight: '1.1764705882',
      fontWeight: 700,
      letterSpacing: 0,
      fontFamily: 'Roboto,sans-serif',
      color: '#1D1D1D',
      '@media (min-width:1200px)': { fontSize: '2.125rem' },
    },
    h2: {
      fontSize: '1.75rem',
      lineHeight: '1.1764705882',
      fontWeight: 600,
      letterSpacing: 0,
      fontFamily: 'Roboto,sans-serif',
      '@media (min-width:1200px)': { fontSize: '2.125rem' },
    },
    h3: {
      fontSize: '1.5rem',
      lineHeight: '1.2857142857',
      fontWeight: 700,
      letterSpacing: 0,
      fontFamily: 'Roboto,sans-serif',
      '@media (min-width:1200px)': { fontSize: '1.75rem' },
    },
    h4: {
      fontSize: '1.75rem',
      lineHeight: '1.4285714286',
      fontWeight: 700,
      letterSpacing: 0,
      fontFamily: 'Roboto,sans-serif',
      color: '#1D1D1D',
    },
    h5: {
      fontSize: '1.125rem',
      lineHeight: '1.3888888889',
      fontWeight: 700,
      letterSpacing: 0,
      fontFamily: 'Roboto,sans-serif',
    },
    h6: {
      fontSize: '.75rem',
      lineHeight: '1.2',
      fontWeight: 700,
      fontFamily: 'Roboto,sans-serif',
    },
    body1: {
      fontSize: 18,
      lineHeight: '1.3888888889',
      fontWeight: 400,
      letterSpacing: 0,
      fontFamily: 'Roboto,sans-serif',
      color: '#495057',
    },
    body2: {
      fontSize: 16,
      lineHeight: '1.4444444444',
      fontWeight: 400,
      letterSpacing: 0,
      fontFamily: 'Roboto,sans-serif',
    },
    button: {
      fontSize: 16,
      fontWeight: 500,
      letterSpacing: 0,
      textTransform: 'none' as const,
      fontFamily: 'Roboto,sans-serif',
      lineHeight: '1.5625',
    },
    subtitle1: {
      fontFamily: 'Roboto,sans-serif',
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: '1.7857142857',
      letterSpacing: 0,
    },
    subtitle2: {
      fontFamily: 'Roboto,sans-serif',
      fontWeight: 700,
      fontSize: '0.875rem',
      lineHeight: '1.7857142857',
      letterSpacing: 0,
    },
    caption: {
      fontFamily: 'Roboto,sans-serif',
      fontWeight: 300,
      fontSize: '0.75rem',
      lineHeight: 1.66,
    },
    overline: {
      fontFamily: 'Roboto,sans-serif',
      fontWeight: 300,
      fontSize: '0.75rem',
      lineHeight: 2.66,
      textTransform: 'uppercase' as const,
    },
  },
  custom: {
    outlinedBorder: '#80a5c9',
    borderDefault: '#929292',
    borderFocused: '#666666',
    borderDivider: '#d3d3d3',
    chipBg: '#f2f6fa',
    errorBg: '#fdf3f3',
    textBody: '#1c1c1c',
    textMuted: '#515151',
    disabled: '#b0bbc9',
    footerLinkHover: '#e7ecef',
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        h4: {
          '&.MuiTypography-h4': {
            color: '#1D1D1D',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          height: 48,
          borderRadius: 0,
          color: '#515151',
          fontSize: 18,
          fontWeight: 400,
          fontFamily: 'Roboto, sans-serif',
          lineHeight: '25px',
          letterSpacing: 0,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#929292',
            borderWidth: 1,
          },
          '&:hover:not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
            borderColor: '#929292',
            borderWidth: '1px !important',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#666666',
            borderWidth: 2,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d40511',
            borderWidth: 1,
          },
          '&.Mui-disabled': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#b0bbc9',
              borderWidth: 1,
            },
          },
          '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus':
            {
              WebkitBoxShadow: '0 0 0 1000px #FFFFFF inset',
              WebkitTextFillColor: '#515151',
              caretColor: '#515151',
              transition: 'background-color 5000s ease-in-out 0s',
            },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          '&.Mui-error': {
            color: '#aa040e',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.MuiInputLabel-root': {
            fontSize: 18,
            fontWeight: 400,
            lineHeight: '1.3888888889',
            letterSpacing: 0,
            fontFamily: 'Roboto,sans-serif',
            color: '#1D1D1D',
            '& .MuiFormLabel-asterisk': {
              color: '#D40511',
            },
          },
          '&.Mui-disabled': {
            color: '#b0bbc9',
          },
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        color: 'primary' as const,
        fullWidth: true,
        size: 'small' as const,
      },
    },
    MuiFormLabel: {
      defaultProps: {
        color: 'primary' as const,
      },
      styleOverrides: {
        root: {
          marginBottom: '4px',
          fontSize: 18,
          fontWeight: 400,
          lineHeight: '1.3888888889',
          letterSpacing: 0,
          fontFamily: 'Roboto,sans-serif',
          color: '#1D1D1D',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltipPlacementTop: {
          marginBottom: '0px !important',
          '& .MuiTooltip-arrow': {
            marginBottom: '-0.71em !important',
          },
        },
        tooltipPlacementBottom: {
          marginTop: '0px !important',
          '& .MuiTooltip-arrow': {
            marginTop: '-0.71em !important',
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        color: 'primary' as const,
        IconComponent: KeyboardArrowDownIcon,
      },
      styleOverrides: {
        root: {
          borderRadius: '4px',
        },
        select: {
          display: 'flex',
          alignItems: 'center',
          lineHeight: '28px',
          paddingTop: 6,
          paddingBottom: 6,
          '& > *': { marginRight: '8px' },
        },
        icon: {
          width: 28,
          height: 28,
          color: '#000000',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained' as const,
        color: 'primary' as const,
      },
      styleOverrides: {
        text: {
          '&:hover': {
            background: 'none',
            color: '#d40511',
          },
        },
        outlined: {
          padding: 24,
          borderRadius: 0,
          lineHeight: 1.35,
          boxShadow: '0 2px 10px 0 rgba(0,0,0,0.08)',
          '&:hover': {
            background: 'none',
          },
        },
        contained: {
          padding: 24,
          borderRadius: 0,
          lineHeight: 1.35,
          boxShadow: '0 2px 10px 0 rgba(0,0,0,0.08)',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: 'rgb(49, 108, 167)',
          },
        },
        containedSecondary: {
          backgroundColor: '#ffffff',
          color: '#004992',
          '&:hover': {
            backgroundColor: '#ffffff',
            color: '#d40511',
          },
        },
        sizeSmall: {
          paddingTop: 5,
          paddingBottom: 3,
          fontSize: '0.875rem',
        },
        sizeLarge: {
          paddingTop: 7.5,
          paddingBottom: 5.5,
          fontSize: '1.25rem',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: '6px 4px',
          position: 'relative',
          '&:hover': { backgroundColor: 'transparent' },
          '&.Mui-focusVisible::before': {
            borderColor: '#004B93',
            boxShadow: '0 0 0 2px #004B93',
          },
          // Hide default SVG icons (keep for sizing)
          '& .MuiSvgIcon-root': {
            width: 24,
            height: 24,
            visibility: 'hidden',
          },
          // Unchecked box
          '&::before': {
            content: '""',
            position: 'absolute',
            width: 24,
            height: 24,
            borderRadius: '4px',
            border: '2px solid #6693BE',
            boxSizing: 'border-box',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          },
          // Checked box
          '&.Mui-checked::before': {
            backgroundColor: '#004B93',
            borderColor: '#004B93',
          },
          // Check mark
          '&.Mui-checked::after': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 8,
            height: 12,
            border: 'solid #fff',
            borderWidth: '0 2px 2px 0',
            transform: 'translate(-50%, -60%) rotate(45deg)',
          },
          // Indeterminate box
          '&.MuiCheckbox-indeterminate::before': {
            backgroundColor: '#004B93',
            borderColor: '#004B93',
          },
          // Indeterminate dash
          '&.MuiCheckbox-indeterminate::after': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 10,
            height: 2,
            backgroundColor: '#fff',
            transform: 'translate(-50%, -50%)',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: 4,
          position: 'relative',
          '&:hover': { backgroundColor: 'transparent' },
          '&.Mui-focusVisible::before': {
            borderColor: '#004B93',
            boxShadow: '0 0 0 2px #004B93',
          },
          // Hide default SVG icons (keep for sizing)
          '& .MuiSvgIcon-root': {
            width: 24,
            height: 24,
            visibility: 'hidden',
          },
          // Outer circle
          '&::before': {
            content: '""',
            position: 'absolute',
            width: 24,
            height: 24,
            borderRadius: '50%',
            border: '2px solid #6693BE',
            boxSizing: 'border-box',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          },
          // Inner circle (selected)
          '&.Mui-checked::after': {
            content: '""',
            position: 'absolute',
            width: 14,
            height: 14,
            borderRadius: '50%',
            backgroundColor: '#004B93',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        label: {
          fontFamily: 'Roboto, sans-serif',
          fontWeight: 400,
          fontSize: 16,
          lineHeight: '25px',
          letterSpacing: 0,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardError: {
          borderRadius: 0,
          borderLeft: '4px solid',
          borderLeftColor: '#d40511',
          backgroundColor: '#fff',
          boxShadow:
            '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
          '& .MuiAlert-icon': { color: '#d40511' },
        },
      },
    },
  },
});

export default theme;
